<template>
  <v-app theme="pdsLightTheme">
    <EgovPublicHeader />
    <v-main id="main" ref="main">
      <v-container v-if="shoppingCartStore.cartUpdateError">
        <EgovInPageAlert
          title="Validation Error"
          type="critical"
          :body-copy="shoppingCartStore.cartUpdateError"
          role="alert"
          aria-live="assertive"
          @close="shoppingCartStore.cartUpdateError = ''"
        />
      </v-container>
      <Suspense><RouterView /></Suspense>
    </v-main>
    <EgovPublicFooter
      :copyright="'Copyright © ' + currentYear + ' Commonwealth of Pennsylvania. All rights reserved.'"
      link-label1="Accessibility"
      link-label2="Privacy & Disclaimers"
      link-label3="Translation Disclaimer"
      link-label4="Take a Survey"
      link-label5="Contact Us"
      :href="footerLinks"
    />
    <EgovProgressSpinner :show="spinner.isLoading" />
    <EgovTimeoutPopup
      v-if="
        userLogin.loginResponse !== null || userLogin.odLoginResponse !== null || userLogin.preVerifyLoginId != null
      "
      v-model="userLogin.showTimeoutPopup"
      :secondsRemaining="userLogin.secondsRemaining"
      @logout="userLogin.logout"
      @continue="userLogin.continueSession"
    />
    <EgovSnackbar />
  </v-app>
</template>

<script setup>
import EgovPublicFooter from '@/components/common/EgovPublicFooter.vue'
import EgovPublicHeader from '@/components/common/EgovPublicHeader.vue'
import EgovSnackbar from '@/components/common/EgovSnackbar.vue'
import EgovInPageAlert from '@/components/common/EgovInPageAlert.vue'
import { useShoppingCartStore } from '@/stores/shoppingCart'
import EgovTimeoutPopup from '@/components/common/EgovTimeoutPopup.vue'
import EgovProgressSpinner from '@/components/common/EgovProgressSpinner.vue'
import { getTodayDate } from '@/util/formatters'
import { useNavigationLinks } from '@/composables/useNavigationLinks'
import { useUserLogin } from '@/stores/userLogin'
import { useSpinner } from '@/stores/spinner'
import { registerAxiosSpinnerInterceptors } from './stores/axiosSpinner'
import { useRouter } from 'vue-router'
import { computed, watch } from 'vue'

const { useNavLink } = useNavigationLinks()

const { url: accessibilityLink } = useNavLink('NAVACCESSIBILITY')
const { url: privacyLink } = useNavLink('NAVPRIVACY')
const { url: translationLink } = useNavLink('NAVTRANSLATION')
const { url: contactUsLink } = useNavLink('NAVCONTACTUS')
const { url: takeSurveyLink } = useNavLink('TAKEASURVEY')

const footerLinks = computed(() => [
  accessibilityLink.value,
  privacyLink.value,
  translationLink.value,
  takeSurveyLink.value,
  contactUsLink.value,
])

const userLogin = useUserLogin()
const spinner = useSpinner()
const shoppingCartStore = useShoppingCartStore()
const router = useRouter()

const currentYear = getTodayDate().slice(0, 4)

registerAxiosSpinnerInterceptors()

router.beforeEach(() => {
  spinner.stop()
  spinner.start()
})
router.afterEach((to, from, failure) => {
  spinner.stop()
  if (!failure) shoppingCartStore.cartUpdateError = ''
})
router.onError(() => spinner.stop())

watch(
  () => shoppingCartStore.cartUpdateError,
  (error) => {
    if (error) window.scrollTo({ top: 0, behavior: 'smooth' })
  },
  { flush: 'post' },
)

watch(
  () => userLogin.loginResponse,
  (response) => {
    if (response !== null) userLogin.startSession()
  },
)

watch(
  () => userLogin.odLoginResponse,
  (response) => {
    if (response !== null) userLogin.startSession()
  },
)

watch(
  () => userLogin.esLoginResponse,
  (response) => {
    if (response !== null) userLogin.startSession()
  },
)

watch(
  () => userLogin.preVerifyLoginId,
  (response) => {
    if (response) userLogin.startSession()
  },
)
</script>

<style lang="scss" scoped>
.v-main {
   --v-layout-top:0px !important;
   border-top: unset;
}
</style>
